/**
 * TQ #8504 — "KI-Einstellungen"-Tab im Einstellungen-Bereich (Cockpit/Org).
 *
 * Konsumiert die GETEILTE, praesentationale Form `@it-sce/aria-settings-form`
 * (auch vom Hub/Personal genutzt) fuer den Org-Default `ai_mode`.
 *
 * Scope-Regeln (Org-Admin):
 * - `allowInheritAiMode={false}` — die Org SETZT den Default selbst, sie erbt
 *   ihn nicht (das "Org-Standard erben" gilt nur fuer den per-User-Override im
 *   Hub/Personal).
 * - `privacy_mode` wird hier `hidden` gerendert. Die massgebliche privacy_mode-
 *   UI ist die reichhaltige PrivacyModeTab (#8466, Tier-Karten + DSGVO-Consent-
 *   Modal) im "Datenschutz"-Tab. Es gibt damit KEINE doppelte privacy_mode-
 *   Steuerung — die shared Form deckt im Cockpit ausschliesslich `ai_mode`.
 *
 * Persistenz: duenner Org-Wrapper api.getOrgAiMode / api.setOrgAiMode
 * (Org-Scope ueber /api/org/{slug}/aria-settings — KEINE me/Personal-Settings).
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { AriaSettingsForm, type AriaSettingsValues } from '@it-sce/aria-settings-form'
import { api } from '../../services/api'

interface Props {
  slug: string
}

// TQ #337 (25.09.2026): AVV-Hinweis am KI-Modus-Schalter — verlinkt auf den
// Verträge-Tab im Dokumente-Bereich (Verknüpfung 2 aus dem Auftrag).
function AvvStatusHint({ status }: { status: 'signed' | 'missing' | 'unknown' | null }) {
  if (status === null) return null
  const style =
    status === 'signed'
      ? { background: '#d1fae5', color: '#065f46' }
      : status === 'missing'
        ? { background: '#fee2e2', color: '#991b1b' }
        : { background: '#e5e7eb', color: '#374151' }
  const label =
    status === 'signed' ? 'AVV liegt vor' : status === 'missing' ? 'AVV fehlt' : 'AVV-Status unbekannt'
  return (
    <p className="aria-org-section-hint" style={{ marginTop: '0.75rem' }}>
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 999,
        fontSize: '0.75rem', fontWeight: 600, marginRight: '0.5rem', ...style,
      }}>
        {label}
      </span>
      <Link to="../dokumente/vertraege" relative="path" style={{ color: '#1a73e8' }}>
        Verträge ansehen →
      </Link>
    </p>
  )
}

export default function OrgAiSettingsTab({ slug }: Props) {
  const { token } = useAuth()
  // privacy_mode wird im Cockpit ueber die shared Form NICHT gesteuert
  // (hidden), bleibt aber im Wert-Objekt als no-op Platzhalter.
  const [values, setValues] = useState<AriaSettingsValues>({ ai_mode: null, privacy_mode: 'eu_only' })
  const [avvStatus, setAvvStatus] = useState<'signed' | 'missing' | 'unknown' | null>(null)
  const [avvConfirmedAt, setAvvConfirmedAt] = useState<string | null>(null)
  // TQ #320: Wechsel auf 'cloud' braucht explizite AVV-Zustimmung (Cortex
  // lehnt sonst mit 400 ab). Solange keine avv_confirmed_at vorliegt, wird der
  // Wechsel erst per Dialog bestaetigt statt sofort gespeichert.
  const [pendingCloudConfirm, setPendingCloudConfirm] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    api.setToken(token)
    setLoading(true)
    api
      .getOrgAiMode(slug)
      .then((r) => {
        if (cancelled) return
        setValues((prev) => ({ ...prev, ai_mode: r.ai_mode }))
        setAvvStatus(r.avv_erp_status)
        setAvvConfirmedAt(r.avv_confirmed_at)
      })
      .catch(() => {
        if (!cancelled) setError('KI-Einstellung konnte nicht geladen werden.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token, slug])

  const persist = async (aiMode: AriaSettingsValues['ai_mode'], avvConfirmed: boolean) => {
    if (!token || saving) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      api.setToken(token)
      const result = await api.setOrgAiMode(slug, aiMode, avvConfirmed)
      setValues((prev) => ({ ...prev, ai_mode: result.ai_mode }))
      setAvvConfirmedAt(result.avv_confirmed_at)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch {
      setError('Speichern fehlgeschlagen.')
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (next: AriaSettingsValues) => {
    // Wechsel auf 'cloud' ohne vorherige AVV-Bestaetigung: Formularwert noch
    // NICHT uebernehmen, sondern erst den Consent-Dialog zeigen. Alles andere
    // (inkl. Zurueckwechseln auf 'local') speichert sofort wie bisher.
    if (next.ai_mode === 'cloud' && !avvConfirmedAt) {
      setPendingCloudConfirm(true)
      return
    }
    setValues(next)
    void persist(next.ai_mode, next.ai_mode === 'cloud')
  }

  const confirmCloud = () => {
    setPendingCloudConfirm(false)
    setValues((prev) => ({ ...prev, ai_mode: 'cloud' }))
    void persist('cloud', true)
  }

  if (loading) {
    return (
      <section className="aria-org-section">
        <p className="aria-org-section-hint">Lade KI-Einstellung…</p>
      </section>
    )
  }

  return (
    <section className="aria-org-section">
      <h3>KI-Modus (Organisations-Standard)</h3>
      <p className="aria-org-section-hint">
        Legt fest, mit welchem Modell-Typ Aria standardmaessig fuer Ihre
        Organisation arbeitet. Mitglieder koennen dies (sofern erlaubt) je
        Konversation ueberschreiben. Wo Anfragen verarbeitet werden duerfen,
        steuert der Tab „Datenschutz".
      </p>
      <div style={{ margin: '1rem 0' }}>
        <AriaSettingsForm
          values={values}
          onChange={handleChange}
          allowInheritAiMode={false}
          fields={{ privacy_mode: { hidden: true } }}
        />
      </div>
      <AvvStatusHint status={avvStatus} />
      {saving && <p className="aria-org-section-hint">Speichere…</p>}
      {saved && (
        <p className="aria-org-section-hint" style={{ color: '#059669' }}>
          ✓ Gespeichert
        </p>
      )}
      {error && (
        <p className="aria-org-section-hint" style={{ color: '#dc2626' }}>
          {error}
        </p>
      )}
      {values.ai_mode === 'cloud' && avvConfirmedAt && (
        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
          AVV-Zustimmung bestätigt am{' '}
          {new Date(avvConfirmedAt).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}
        </div>
      )}

      {pendingCloudConfirm && (
        <div
          style={{
            marginTop: '1rem',
            background: '#fafafa',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            padding: '1rem 1.25rem',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827', marginBottom: '0.5rem' }}>
            Cloud-KI freigeben — Auftragsverarbeitung (AVV)
          </div>
          <p style={{ fontSize: '0.8125rem', color: '#374151', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
            Im Cloud-Modus verarbeiten leistungsstarke externe Anbieter Anfragen Ihrer
            Organisation auf Basis vertraglicher Zusicherungen (Auftragsverarbeitung, kein
            Training mit Ihren Daten). <strong>Ihre Daten können dabei unsere eigene Infrastruktur
            verlassen</strong> — dafür ist Ihre ausdrückliche Zustimmung erforderlich. Sie können
            jederzeit auf „Lokal" zurückwechseln.
          </p>
          <div style={{ display: 'flex', gap: '0.625rem' }}>
            <button
              onClick={confirmCloud}
              disabled={saving}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: 6,
                border: 'none',
                background: '#6366f1',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.8125rem',
                cursor: saving ? 'default' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Wird gespeichert…' : 'Bestätigen & Freigeben'}
            </button>
            <button
              onClick={() => setPendingCloudConfirm(false)}
              disabled={saving}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: 'white',
                color: '#374151',
                fontWeight: 500,
                fontSize: '0.8125rem',
                cursor: 'pointer',
              }}
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
